import api from "./api";

const STORAGE_KEY = "permissions";

/** Fetch the current user's effective permission rules (allow/deny) from the backend. */
const fetchMyPermissions = () => {
  return api.get("/api/auth/me/permissions");
};

/** Full permission catalog (for Roles/Policies pickers). */
const getCatalog = () => {
  // Must be /api-prefixed: deployed environments route only /api/* to the
  // backend; anything else falls through to the SPA and returns index.html.
  return api.get("/api/settings/permission/catalog");
};

/**
 * Persist the resolved allow/deny rule sets for synchronous can() checks.
 * Stored as `{ allows: string[], denies: string[], branchScopes }`, where
 * `branchScopes` maps a branch-scoped key held only for some branches to those
 * branch ids (read by canInBranches()).
 */
const savePermissions = (rules) => {
  const allows = Array.isArray(rules?.allows) ? rules.allows : [];
  const denies = Array.isArray(rules?.denies) ? rules.denies : [];
  const branchScopes = rules?.branchScopes && typeof rules.branchScopes === "object" ? rules.branchScopes : {};
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ allows, denies, branchScopes }));
};

/** Returns `{ allows, denies }` (always arrays), tolerating legacy/empty storage. */
const getPermissions = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY));
    // Legacy format was a flat string[] of allowed keys; treat it as allows.
    if (Array.isArray(parsed)) return { allows: parsed, denies: [] };
    return {
      allows: Array.isArray(parsed?.allows) ? parsed.allows : [],
      denies: Array.isArray(parsed?.denies) ? parsed.denies : [],
    };
  } catch {
    return { allows: [], denies: [] };
  }
};

const clearPermissions = () => {
  localStorage.removeItem(STORAGE_KEY);
};

/** Fetch + persist in one step (call right after login). */
const loadAndStore = async () => {
  const res = await fetchMyPermissions();
  const rules = {
    allows: res?.data?.allows ?? [],
    denies: res?.data?.denies ?? [],
    branchScopes: res?.data?.branchScopes ?? {},
  };
  savePermissions(rules);
  return rules;
};

const PermissionService = {
  fetchMyPermissions,
  getCatalog,
  savePermissions,
  getPermissions,
  clearPermissions,
  loadAndStore,
};

export default PermissionService;
